import type { DescriptionBlock } from '@/units/ds/utils/description-block.type'

/**
 * Parses a free-text work-experience description into render blocks for the CV.
 *
 * Lines starting with `- ` or `* ` become bullet items; consecutive bullet
 * lines are grouped into a single `list` block. Everything else is kept as
 * `text` with its line breaks preserved. Returns an empty array for blank
 * input so nothing is rendered.
 *
 * The text is intentionally NOT turned into HTML — the caller renders each
 * item via Vue interpolation (auto-escaped), which keeps the CV-to-PDF HTML
 * free of injected markup.
 */
const BULLET = /^\s*[-*]\s+(.*)$/

export function parseDescriptionBlocks(
  text: string | null | undefined,
): DescriptionBlock[] {
  if (!text || text.trim() === '') {
    return []
  }

  const blocks: DescriptionBlock[] = []

  for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
    const bulletMatch = line.match(BULLET)
    const lastBlock = blocks[blocks.length - 1]

    if (bulletMatch) {
      const item = bulletMatch[1].trim()
      if (lastBlock?.type === 'list') {
        lastBlock.items.push(item)
      } else {
        blocks.push({ type: 'list', items: [item] })
      }
    } else if (lastBlock?.type === 'text') {
      lastBlock.items.push(line)
    } else {
      blocks.push({ type: 'text', items: [line] })
    }
  }

  return blocks
}
